import { useEffect, useState } from 'react';
import DetailDialog from '@/shared/components/DetailDialog/DetailDialog';
import { useMfsCatalog } from './useMfsCatalog';
import { emptyDraft, draftRows, validateDraft, makeDraftSnapshot } from './mfsDraft.model';
import ScopeSelection from './ScopeSelection';
import TechniqueAssignments from './TechniqueAssignments';
import styles from './MfsDraftWizard.module.css';

const steps = ['Details', 'Project scope', 'Techniques', 'Review'];

export default function MfsDraftWizard({ companyId, siteId, assetId, initial, otherDrafts = [], onSave, onClose }) {
  const resource = useMfsCatalog(companyId);
  const [baseline] = useState(() => JSON.stringify(initial?.draft || emptyDraft()));
  const [draft, setDraft] = useState(() => JSON.parse(baseline));
  const [step, setStep] = useState(0);
  const [error, setError] = useState('');
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [openingScope] = useState({ siteId, assetId });
  const dirty = JSON.stringify(draft) !== baseline;
  const catalog = resource.data?.rows || [];
  const selected = draftRows(draft, catalog);
  const overlaps = selected.filter(row => otherDrafts.some(item => item.id !== initial?.id && item.components.some(component => component.key === row.key))).length;

  useEffect(() => {
    if (!dirty) return;
    const warn = event => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  function close() { if (dirty) setConfirmDiscard(true); else onClose(); }
  function change(key, value) { setDraft(previous => ({ ...previous, [key]: value })); }
  function submit(event) {
    event.preventDefault();
    try {
      if (resource.isLoading || resource.error || !resource.data) throw new Error('Load the component catalogue before continuing.');
      validateDraft(draft, catalog, Math.min(step, 2));
      setError('');
      if (step < 3) setStep(previous => previous + 1);
      else onSave(makeDraftSnapshot(draft, catalog, companyId, initial?.id || 'draft-' + crypto.randomUUID()));
    } catch (failure) { setError(failure.message); }
  }

  return <DetailDialog eyebrow="MAINTENANCE SCHEDULE DRAFT" title={initial ? 'Edit local draft' : 'Draft maintenance schedule'}
    subtitle="Planning only — no inspection records are created" onClose={close}>
    <div className={styles.container}>
      <ol className={styles.steps}>{steps.map((label, index) => <li key={label} aria-current={step === index ? 'step' : undefined}>
        <span>{index + 1}</span>{label}
      </li>)}</ol>
      {confirmDiscard && <div className={styles.notice} role="alert">
        <p>Discard the unsaved changes to this draft?</p><div className={styles.actions}>
          <button type="button" className={styles.button} onClick={() => setConfirmDiscard(false)}>Keep editing</button>
          <button type="button" className={styles.button} onClick={onClose}>Discard changes</button>
        </div>
      </div>}
      {resource.isLoading && <p role="status">Loading the company component catalogue…</p>}
      {resource.error && <div className={styles.notice}><p role="alert">{resource.error}</p>
        <button type="button" className={styles.button} onClick={() => resource.reload()}>Retry catalogue</button></div>}
      {resource.data?.excluded > 0 && <p className={styles.notice}>{resource.data.excluded} component record(s) cannot be offered because their location or identity is incomplete. This catalogue is partial.</p>}
      <form onSubmit={submit}>
        {step === 0 && <div className={styles.fields}>
          <label className={styles.full}>Schedule name<input required maxLength={120} value={draft.name} onChange={event => change('name', event.target.value)} /></label>
          <label>Accountable owner<input required maxLength={80} value={draft.owner} onChange={event => change('owner', event.target.value)} /></label>
          <label>First due date<input required type="date" value={draft.due} onChange={event => change('due', event.target.value)} /></label>
          <label>Repeat every (months)<input required type="number" min="1" max="120" step="1" value={draft.frequency} onChange={event => change('frequency', event.target.value)} /></label>
          <label>Starting technique<select value={draft.startingTechnique} onChange={event => change('startingTechnique', event.target.value)}>
            {draft.techniques.map(value => <option key={value}>{value}</option>)}</select></label>
          <p className={styles.full}>The starting technique applies when a component is first selected. Existing assignments stay unchanged.</p>
          <label className={styles.full}>Scope notes<textarea rows={3} maxLength={4000} value={draft.notes} onChange={event => change('notes', event.target.value)} /></label>
        </div>}
        {step === 1 && <ScopeSelection catalog={catalog} draft={draft} setDraft={setDraft}
          initialSiteId={openingScope.siteId} initialAssetId={openingScope.assetId} />}
        {step === 2 && <TechniqueAssignments catalog={catalog} draft={draft} setDraft={setDraft} />}
        {step === 3 && <div className={styles.review}>
          <h3>{draft.name}</h3><p>{draft.owner} · First due {draft.due} · Every {draft.frequency} months</p>
          <p><strong>{selected.length} components · {new Set(selected.map(row => row.siteId)).size} sites · {selected.reduce((sum, row) => sum + (draft.assignments[row.key]?.length || 0), 0)} component–technique assignments</strong></p>
          <div className={styles.reviewList}>{selected.map(row => <div key={row.key}>
            <span>{row.siteLabel} / {row.assetLabel}<strong>{row.code} · {row.name}</strong></span>
            <span>{(draft.assignments[row.key] || []).join(' · ')}</span>
          </div>)}</div>
          {overlaps > 0 && <p>{overlaps} components also occur in another local draft. These schedules remain independent. Existing backend schedule overlaps are not available.</p>}
          {draft.notes && <p>{draft.notes}</p>}
          <p className={styles.notice}>Saving keeps a draft in this Inspection Suite workspace only. Export it before leaving or refreshing. It does not create an active MFS, recurring occurrences or inspection items.</p>
        </div>}
        {error && <p className={styles.error} role="alert">{error}</p>}
        <div className={styles.footer}>
          <button type="button" className={styles.button} onClick={() => {
            if (step) { setStep(previous => previous - 1); setError(''); } else close();
          }}>{step ? 'Back' : 'Cancel'}</button>
          <span>Step {step + 1} of {steps.length}</span>
          <button type="submit" className={styles.primary} disabled={resource.isLoading || Boolean(resource.error) || !resource.data}>
            {step === 3 ? 'Save local draft' : 'Continue'}
          </button>
        </div>
      </form>
    </div>
  </DetailDialog>;
}